import type { ScheduleItem, ScheduleProjection } from "@/components/schedule/types";
export interface NavigableHeat {
  id: string;
  division_id?: string | null;
  round_id?: string | null;
  sort_order?: number;
  scheduled_at?: string | null;
}
export function orderedHeats<T extends NavigableHeat>(heats: readonly T[]): T[] {
  return [...heats].sort((a, b) => {
    const time = (value: string | null | undefined) => (value ? Date.parse(value) : Infinity);
    const delta = time(a.scheduled_at) - time(b.scheduled_at);
    return (
      (Number.isNaN(delta) ? 0 : delta) ||
      (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
      a.id.localeCompare(b.id)
    );
  });
}
export function groupHeats<T extends NavigableHeat>(heats: readonly T[]) {
  const groups: { divisionId: string | null; roundId: string | null; heats: T[] }[] = [];
  for (const heat of orderedHeats(heats)) {
    const divisionId = heat.division_id ?? null,
      roundId = heat.round_id ?? null;
    let group = groups.find((g) => g.divisionId === divisionId && g.roundId === roundId);
    if (!group) {
      group = { divisionId, roundId, heats: [] };
      groups.push(group);
    }
    group.heats.push(heat);
  }
  return groups;
}
export function scheduleItems(
  timeline: ScheduleProjection | null | undefined,
  areaId: string | null,
): ScheduleItem[] {
  return (
    timeline?.days.flatMap((day) =>
      day.areas.filter((area) => area.id === areaId).flatMap((area) => area.items),
    ) ?? []
  );
}
export function nextScheduled(
  timeline: ScheduleProjection | null | undefined,
  areaId: string | null,
) {
  return scheduleItems(timeline, areaId).find(
    (item) => item.kind === "heat" && item.status === "pending",
  );
}
export function scheduleLabel(item: ScheduleItem | undefined, timezone = "UTC") {
  if (!item) return "";
  const time = (value: string) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  return [
    item.planned && `Scheduled ${time(item.planned)}`,
    item.estimated && `ETA ${time(item.estimated)}`,
  ]
    .filter(Boolean)
    .join(" · ");
}
